import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  CircularProgress,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { ReactComponent as HubIcon } from '../../../../../images/icons/hub_icon.svg';
import { ReactComponent as UpdateSwSuccessIcon } from '../../../../../images/icons/update_sw_success.svg';
import { ReactComponent as UpdateSwFailedIcon } from '../../../../../images/icons/update_sw_failed.svg';
import NewModal from '../../../../Shared/NewModal';
import CustomerCareSelect from '../../../../Shared/CustomerCareSelect';
import { dimGrey } from '../../../../../mainTheme';
import { useDispatch, useSelector } from 'react-redux';
import { isNull } from 'lodash';
import { RootState } from '../../../../../store';
import { DevicesStore } from '../../../../../store/devices/deviceTypes';
import { SoftwareVersion } from '../../../../../store/software/softwareTypes';
import { getSoftwareVersions } from '../../../../../store/software/actionCreators';
import {
  clearDeviceActionStatus,
  EnvSwitchPayload,
  sendEnvSwitch,
} from '../../../../../store/devices/actionCreators';
import {
  ENV_SWITCH_REST_HOSTS,
  ENV_SWITCH_STORAGE_HOSTS,
  ENV_SWITCH_TARGETS,
  EnvSwitchTarget,
  SOC_TYPE,
} from '../../../../../shared/constants';

const typography = {
  heading: {
    fontWeight: 500,
    fontSize: '22px',
    lineHeight: '30px',
    letterSpacing: '0.36px',
  },
  subText: {
    fontWeight: 500,
    fontSize: '16px',
    lineHeight: '22px',
    letterSpacing: '0.2px',
    color: dimGrey,
  },
} as const;

interface EnvSwitchDialogProps {
  onClose: () => void;
  deviceId: number;
  deviceSocId: number;
}

interface StatusViewProps {
  icon: React.ReactElement;
  title: string;
  message: string;
}

const StatusView: React.FC<StatusViewProps> = ({ icon, title, message }) => (
  <Box display="flex" flexDirection="column" alignItems="center">
    {icon}
    <Typography sx={{ ...typography.heading, mt: 2 }}>{title}</Typography>
    <Typography sx={{ ...typography.subText, textAlign: 'center' }}>
      {message}
    </Typography>
  </Box>
);

const VERSION_RE = /^\d+\.\d+\.\d+(\.\d+)?(-[\w.]+)?(\+[\w.]+)?$/;

// storage.<env>.omcare.com only serves /root_image* (StorageDnsRestriction);
// the imx8 sub-path is the api-key-gated route. A /firmware/ path 403s.
const rootImageBase = (env: EnvSwitchTarget, socId: number) =>
  `${ENV_SWITCH_STORAGE_HOSTS[env]}/root_image${
    socId === SOC_TYPE.IMX8 ? '/imx8' : ''
  }`;

const buildStorageUrl = (
  env: EnvSwitchTarget,
  version: string,
  suffix: string,
  socId: number,
) => `${rootImageBase(env, socId)}/omcare-${version}.rootfs.tar.xz${suffix}`;

const defaultForm = (
  env: EnvSwitchTarget,
  version: string,
  socId: number,
): EnvSwitchPayload => ({
  target_env: env,
  target_rest_host: ENV_SWITCH_REST_HOSTS[env],
  version,
  image_url: version ? buildStorageUrl(env, version, '', socId) : '',
  sig_url: version ? buildStorageUrl(env, version, '.sig', socId) : '',
  key_url: `${rootImageBase(env, socId)}/anser_gpg.asc`,
});

export const EnvSwitchDialog: React.FC<EnvSwitchDialogProps> = ({
  onClose,
  deviceId,
  deviceSocId,
}) => {
  const { actionStatus, softwareVersions, isSoftwareLoading } = useSelector<
    RootState,
    {
      actionStatus: DevicesStore['actionStatus'];
      softwareVersions: SoftwareVersion[] | null;
      isSoftwareLoading: boolean;
    }
  >((state) => ({
    actionStatus: state.devices.actionStatus,
    softwareVersions: state.software.softwareVersions,
    isSoftwareLoading: state.software.isLoading,
  }));

  const filteredVersions = useMemo(
    () => (softwareVersions ?? []).filter((v) => v.soc_id === deviceSocId),
    [softwareVersions, deviceSocId],
  );

  const dispatch = useDispatch();
  const [step, setStep] = useState<'form' | 'success' | 'failed'>('form');
  const [versionMode, setVersionMode] = useState<'select' | 'manual'>('select');
  const [form, setForm] = useState<EnvSwitchPayload>(
    defaultForm('staging', '', deviceSocId),
  );

  useEffect(() => {
    if (isNull(softwareVersions)) {
      dispatch(getSoftwareVersions(false));
    }
  }, [dispatch, softwareVersions]);

  useEffect(() => {
    if (actionStatus.type === 'env_switch') {
      setStep(actionStatus.success ? 'success' : 'failed');
    }
  }, [actionStatus]);

  const handleEnvChange = (env: EnvSwitchTarget) => {
    setForm((prev) => ({
      ...defaultForm(env, prev.version, deviceSocId),
    }));
  };

  const handleVersionChange = (version: string) => {
    setForm((prev) => ({
      ...prev,
      version,
      image_url: version
        ? buildStorageUrl(prev.target_env, version, '', deviceSocId)
        : '',
      sig_url: version
        ? buildStorageUrl(prev.target_env, version, '.sig', deviceSocId)
        : '',
    }));
  };

  const handleVersionModeChange = (
    _: React.MouseEvent<HTMLElement>,
    next: 'select' | 'manual' | null,
  ) => {
    if (!next) return;
    setVersionMode(next);
    handleVersionChange('');
  };

  const handleFieldChange =
    (field: keyof EnvSwitchPayload) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const isFormValid =
    VERSION_RE.test(form.version) &&
    form.target_rest_host.startsWith('https://') &&
    form.image_url.startsWith('https://') &&
    form.sig_url.startsWith('https://') &&
    form.key_url.startsWith('https://');

  const handleSubmit = () => {
    dispatch(sendEnvSwitch(deviceId, form));
  };

  const handleReset = () => {
    dispatch(clearDeviceActionStatus());
    setStep('form');
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const renderVersionInput = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <ToggleButtonGroup
        value={versionMode}
        exclusive
        onChange={handleVersionModeChange}
        size="small"
        fullWidth
      >
        <ToggleButton value="select">Select version</ToggleButton>
        <ToggleButton value="manual">Enter manually</ToggleButton>
      </ToggleButtonGroup>

      {versionMode === 'select' ? (
        isSoftwareLoading ? (
          <Box display="flex" justifyContent="center" py={1}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <CustomerCareSelect
            label="Version"
            value={form.version}
            onChange={(e) => handleVersionChange(e.target.value as string)}
            fullWidth
          >
            {filteredVersions.map((v) => (
              <MenuItem key={v.id} value={v.version_raw}>
                {v.version_raw}
              </MenuItem>
            ))}
          </CustomerCareSelect>
        )
      ) : (
        <TextField
          label="Version"
          placeholder="1.2.3"
          value={form.version}
          onChange={(e) => handleVersionChange(e.target.value)}
          fullWidth
          size="small"
          helperText="Format: 1.2.3, 1.2.3-rc1, 3.1.2-pr291.15, or 3.1.2-pr291.15+32db9759"
          error={form.version !== '' && !VERSION_RE.test(form.version)}
        />
      )}
    </Box>
  );

  const renderForm = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <HubIcon />
      <Typography sx={typography.heading}>Switch environment</Typography>
      <Typography sx={typography.subText}>
        Issues an OTA command to move the hub to a different environment. The
        hub will re-provision certificates and reboot into the target image.
        Valid targets: <strong>production</strong>, <strong>staging</strong>,{' '}
        <strong>uat</strong>.
      </Typography>

      <CustomerCareSelect
        label="Target environment"
        value={form.target_env}
        onChange={(e) => handleEnvChange(e.target.value as EnvSwitchTarget)}
        fullWidth
      >
        {ENV_SWITCH_TARGETS.map((env) => (
          <MenuItem key={env} value={env}>
            {env}
          </MenuItem>
        ))}
      </CustomerCareSelect>

      {renderVersionInput()}

      <TextField
        label="Target REST host"
        value={form.target_rest_host}
        onChange={handleFieldChange('target_rest_host')}
        fullWidth
        size="small"
        error={!form.target_rest_host.startsWith('https://')}
      />

      <TextField
        label="Image URL"
        value={form.image_url}
        onChange={handleFieldChange('image_url')}
        fullWidth
        size="small"
        error={form.image_url !== '' && !form.image_url.startsWith('https://')}
      />

      <TextField
        label="Signature URL"
        value={form.sig_url}
        onChange={handleFieldChange('sig_url')}
        fullWidth
        size="small"
        error={form.sig_url !== '' && !form.sig_url.startsWith('https://')}
      />

      <TextField
        label="GPG key URL"
        value={form.key_url}
        onChange={handleFieldChange('key_url')}
        fullWidth
        size="small"
        error={!form.key_url.startsWith('https://')}
      />
    </Box>
  );

  const renderContent = () => {
    switch (step) {
      case 'form':
        return renderForm();
      case 'success':
        return (
          <StatusView
            icon={<UpdateSwSuccessIcon />}
            title="Command sent"
            message="The hub will re-provision certificates and reboot into the target environment. Expect an env_switch_complete heartbeat within ~15 minutes."
          />
        );
      case 'failed':
        return (
          <StatusView
            icon={<UpdateSwFailedIcon />}
            title="Command failed"
            message="Please check that the device is online and all URLs are reachable, then try again."
          />
        );
    }
  };

  const actionConfig = {
    form: { text: 'Switch', onClick: handleSubmit, disabled: !isFormValid },
    success: { text: 'OK', onClick: handleClose, disabled: false },
    failed: { text: 'Try again', onClick: handleReset, disabled: false },
  };

  return (
    <NewModal
      showCloseX
      title="Switch environment"
      onClose={handleClose}
      maxWidth="xs"
      actions={{
        primaryAction: actionConfig[step],
        secondaryAction:
          step === 'form'
            ? { text: 'Cancel', onClick: handleClose }
            : undefined,
      }}
    >
      {renderContent()}
    </NewModal>
  );
};
